/**
 * Brand glyphs from Simple Icons (CC0). Rendered white on platform-coloured tiles.
 *
 * Uses <img> + invert (not CSS mask-image): Vite SVG URLs often fail as masks in
 * Chromium/WebKit when -webkit-mask-* size/repeat/position are incomplete or when
 * paths omit an explicit fill — which showed as empty coloured squares.
 */
import type { SocialPlatform } from '@/lib/social-api';
import { platformMeta } from '@/lib/social-platforms';
import { cn } from '@/lib/utils';
import facebookLogo from '@/assets/platforms/facebook.svg';
import googleLogo from '@/assets/platforms/google.svg';
import instagramLogo from '@/assets/platforms/instagram.svg';
import linkedinLogo from '@/assets/platforms/linkedin.svg';
import pinterestLogo from '@/assets/platforms/pinterest.svg';
import redditLogo from '@/assets/platforms/reddit.svg';
import threadsLogo from '@/assets/platforms/threads.svg';
import tiktokLogo from '@/assets/platforms/tiktok.svg';
import xLogo from '@/assets/platforms/x.svg';
import youtubeLogo from '@/assets/platforms/youtube.svg';

const LOGO_SRC: Record<SocialPlatform, string> = {
  linkedin: linkedinLogo,
  instagram: instagramLogo,
  facebook: facebookLogo,
  youtube: youtubeLogo,
  tiktok: tiktokLogo,
  x: xLogo,
  threads: threadsLogo,
  pinterest: pinterestLogo,
  reddit: redditLogo,
  google_business: googleLogo,
};

/** Brand tile backgrounds — Instagram uses its classic gradient. */
function tileBackground(platform: SocialPlatform): string {
  if (platform === 'instagram') {
    return 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';
  }
  if (platform === 'tiktok' || platform === 'x' || platform === 'threads') {
    return '#111111';
  }
  return platformMeta(platform).hex;
}

interface PlatformIconProps {
  platform: SocialPlatform;
  /** Outer tile size in px. Default 36. */
  size?: number;
  className?: string;
  /** Dimmed look for unavailable channels. */
  muted?: boolean;
}

/**
 * Brand logo tile for social platforms (Simple Icons paths, white glyph on brand colour).
 */
export function PlatformIcon({ platform, size = 36, className, muted }: PlatformIconProps) {
  const src = LOGO_SRC[platform] ?? linkedinLogo;
  const glyph = Math.round(size * 0.52);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg shadow-sm',
        muted && 'opacity-55',
        className
      )}
      style={{ width: size, height: size, background: tileBackground(platform) }}
      title={platformMeta(platform).label}
      aria-hidden
    >
      <img
        src={src}
        alt=""
        width={glyph}
        height={glyph}
        draggable={false}
        className="pointer-events-none select-none brightness-0 invert"
      />
    </span>
  );
}
